/** Draft v1 section IDs. Append new IDs after release; never recycle an existing ID. */
export const SECTION_NAMES = [
  "nodeTemplate", "nodeInstance", "nodeReason", "nodeJustification", "nodeValue", "valueOffsets",
  "instanceParent", "instanceEntity", "instanceIndex", "edgeGroupCounts", "edgeGroupTemplates", "edgeGroupSizes",
  "edgeTargetInstances", "reasonOffsets", "nodeState", "stateOffsets", "validationNodes", "validationText",
  "validationTextOffsets", "validationAttributeCounts", "validationAttributes", "ruleOffsets", "ruleEdges",
  "ruleEdgeOffsets", "ruleNodeExtraOffsets", "nodePropertyOffsets", "propertyNodes", "nodePropertyRecords",
  "valueData", "edgeSources", "reasonData", "stateData", "validationShown", "validationTextData", "ruleData",
  "ruleEdgeData", "ruleNodeExtraData", "propertyKeys", "propertyFlags", "nodePropertyData",
] as const;
const signed = new Set(["nodeInstance", "instanceParent", "instanceIndex", "edgeGroupTemplates", "edgeTargetInstances", "validationAttributes"]);
const integers = new Set<string>(SECTION_NAMES.slice(0, 28));
export const integerSection = (name: string) => integers.has(name);
export const signedSection = (name: string) => signed.has(name);
export type UnsignedColumn = Uint8Array | Uint16Array | Uint32Array;
export type SignedColumn = Int8Array | Int16Array | Int32Array;
export type Section = { name: string; offset: number; length: number; width: number };
export type SectionEntry = [number | string, number, number, number?];
export const sectionEntry = (s: Section): SectionEntry => {
  const id = SECTION_NAMES.indexOf(s.name as any);
  return s.width === 4 ? [id < 0 ? s.name : id, s.offset, s.length] : [id < 0 ? s.name : id, s.offset, s.length, s.width];
};
export const readSections = (entries: (SectionEntry | Omit<Section, "width"> & { width?: number })[]): Section[] => entries.map((raw) => {
  // Earlier draft writers used named records; normalize the directory without copying section data.
  const entry = Array.isArray(raw) ? raw : raw && typeof raw === "object"
    ? [raw.name, raw.offset, raw.length, raw.width ?? 4] : raw;
  if (!Array.isArray(entry) || entry.length < 3 || entry.length > 4 ||
      !(typeof entry[0] === "string" || (Number.isSafeInteger(entry[0]) && Number(entry[0]) >= 0))) {
    throw new Error("Invalid decision graph section entry");
  }
  const [id, offset, length, width = 4] = entry as SectionEntry;
  return { name: typeof id === "string" ? id : SECTION_NAMES[id] ?? `unknown:${id}`, offset, length, width };
});
export const columnType = (signed: boolean, width: number) => {
  if (width === 1) return signed ? Int8Array : Uint8Array;
  if (width === 2) return signed ? Int16Array : Uint16Array;
  if (width === 4) return signed ? Int32Array : Uint32Array;
  throw new Error("Invalid decision graph integer width");
};
/** Pick a width without changing integer semantics. Empty sections keep the default width. */
export const narrowSection = (name: string, bytes: Uint8Array): { bytes: Uint8Array; width: number } => {
  if (!integerSection(name) || bytes.length === 0) return { bytes, width: 4 };
  const values = new (signed.has(name) ? Int32Array : Uint32Array)(bytes.buffer as ArrayBuffer, bytes.byteOffset, bytes.length / 4);
  let min = 0, max = 0;
  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    if (value < min) min = value;
    if (value > max) max = value;
  }
  const width = signed.has(name) ? min >= -128 && max <= 127 ? 1 : min >= -32768 && max <= 32767 ? 2 : 4 : max <= 255 ? 1 : max <= 65535 ? 2 : 4;
  if (width === 4) return { bytes, width };
  const Type = columnType(signed.has(name), width);
  return { bytes: new Uint8Array(Type.from(values).buffer), width };
};
